import './BriefDescriptionEditor.scss';

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { PiSmileyFill } from 'react-icons/pi';

import Header from '../../../../../components/Header/Header';
import EmojiPickerModel from '../../../../../components/EmojiPickerModel/EmojiPickerModel';
import { TQ_VALIDATION } from '../../../../TQConnect/constants';

interface Props {
  initialDescription: string;
  placeholder: string;
  onDone: (description: string) => void;
  onClose: () => void;
  title?: string;
  allowEmpty?: boolean;
  showEmojiPicker?: boolean;
}

const BriefDescriptionEditor: React.FC<Props> = ({
  initialDescription,
  placeholder,
  onDone,
  onClose,
  title = 'Brief Description',
  allowEmpty = false,
  showEmojiPicker = false,
}) => {
  const maxLength = TQ_VALIDATION.MAX_SHOUTOUT_DESCRIPTION_LENGTH;
  const [description, setDescription] = useState(initialDescription);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorRef = useRef({ start: 0, end: 0 });

  useEffect(() => {
    if (!initialDescription || !textareaRef.current) return;
    const el = textareaRef.current;
    const len = initialDescription.length;
    const id = requestAnimationFrame(() => {
      el.setSelectionRange(len, len);
    });
    return () => cancelAnimationFrame(id);
  }, [initialDescription]);

  const handleDone = () => {
    onDone(description);
  };

  const openEmojiPicker = useCallback(() => {
    const el = textareaRef.current;
    if (el) {
      const start = el.selectionStart ?? description.length;
      const end = el.selectionEnd ?? description.length;
      cursorRef.current = { start, end };
    }
    setEmojiPickerOpen(true);
  }, [description.length]);

  const handleEmojiSelect = useCallback((emoji: string) => {
    const { start, end } = cursorRef.current;
    const next =
      description.slice(0, start) + emoji + description.slice(end);
    const clamped = next.slice(0, maxLength);
    setDescription(clamped);
    const newCursor = Math.min(start + emoji.length, clamped.length);
    setEmojiPickerOpen(false);
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newCursor, newCursor);
    }, 0);
  }, [description, maxLength]);

  const canDone = allowEmpty || description.trim().length > 0;

  return (
    <div className="brief-description-editor">
      <Header
        title={title}
        handleBack={onClose}
        backType="cross"
        actionItems={[
          <button
            key="done"
            type="button"
            className={`brief-description-editor__done ${canDone ? 'brief-description-editor__done--active' : ''}`}
            onClick={handleDone}
            disabled={!canDone}>
            Done
          </button>,
        ]}
      />
      <div className="brief-description-editor__body">
        <textarea
          ref={textareaRef}
          className="brief-description-editor__textarea"
          placeholder={placeholder}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={maxLength}
          rows={12}
          autoFocus
        />
        {showEmojiPicker ? (
          <button
            type="button"
            className="brief-description-editor__emoji-btn"
            onClick={openEmojiPicker}
            aria-label="Add emoji">
            <PiSmileyFill size={24} fill="var(--text-subtitle, #888)" />
          </button>
        ) : null}
        <div className="brief-description-editor__counter">
          {description.length}/{maxLength}
        </div>
      </div>

      {showEmojiPicker ? (
        <EmojiPickerModel
          show={emojiPickerOpen}
          closeModal={() => setEmojiPickerOpen(false)}
          onSelectEmoji={handleEmojiSelect}
        />
      ) : null}
    </div>
  );
};

export default memo(BriefDescriptionEditor);
